<script lang="ts" setup>
import { ref, onMounted, reactive, watch } from "vue";
import { roundNumber } from "@/DataHelper";
import { Globals } from "@/Globals";
import { globalsBezeichnung, begriffBezeichnung } from "@/local";
interface BlockDef {
  width: string;
  color: string;
  text: string;
  textColor: string;
  unit?: string;
  tooltip?: string;
}

const props = defineProps<{
  value: number;
  overColor?: string;
  underColor?: string;
  remainingColor?: string;
  textColor?: string;
  destination: number;
  height?: number;
  maxTextColor?: string;
  unit?: string;
  tooltip?: string;
  maxTextBackground?: string;
  textWithPercent?: number;
  textValue?: string;
  textDest?: string;
  textDiff?: string;
}>();
let blocks = ref<BlockDef[]>([]);

let percentValue: number = 30;
let text: string = "100";

let oldValue = 0;
let oldDest = 0;
let textIst: string = globalsBezeichnung(Globals.Begriff, "Ist", "Ist");
let textSoll: string = globalsBezeichnung(Globals.Begriff, "Soll", "Soll");
let textMehr: string = globalsBezeichnung(
  Globals.Begriff,
  "Web_Mehrleistung",
  "Mehrleistung"
);
let textoffen: string = globalsBezeichnung(
  Globals.Begriff,
  "Web_offen",
  "offen"
);
onMounted(() => {
  refreshValues();
});

function getStyle(block: BlockDef) {
  return {
    width: block.width,
    background: getBackground(block),
    height: props.height,
    color: block.textColor,
  };
}

function getBackground(block: BlockDef) {
  if (block.color === props.maxTextBackground) {
    return block.color;
  }
  return (
    // "repeating-linear-gradient(45deg,rgba(0, 0, 0, 0.2),rgba(0, 0, 0, 0.2) 10px,
    // rgba(0, 0, 0, 0.3) 10px,rgba(0, 0, 0, 0.3) 20px)," +
    block.color
  );
}

// watch("value");
// watch("destination");
function refreshValues() {
  // nur bei Veränderung verwenden
  if (
    props.value === oldValue &&
    props.destination === oldDest &&
    blocks.value.length > 0
  ) {
    return;
  }
  oldValue = props.value;
  oldDest = props.destination;
  let val = props.value;
  let dest = props.destination;
  if (!val) {
    val = 0;
  }
  if (!dest) {
    const text = begriffBezeichnung(
      "Web_keineDaten_gefunden",
      "keine Daten gefunden"
    );
    blocks.value = [
      {
        width: "100%",
        text: "..." + text + "...",
        color: props.maxTextBackground ?? "transparent",
        textColor: props.maxTextColor ?? "black",
      },
    ];
    return;
  }
  if (props.textDiff) {
    textMehr = props.textDiff;
  }
  if (props.textValue) {
    textIst = props.textValue;
  }
  if (props.textDest) {
    textSoll = props.textDest;
  }
  // bindings kommen oft trotzdem als string daher !!!
  if (typeof val === "string") {
    val = parseFloat(val);
  }
  if (typeof dest === "string") {
    dest = parseFloat(dest);
  } else {
    dest = roundNumber(dest, 2);
  }
  if (val >= dest) {
    const delta = roundNumber(val - dest, 2);
    const maxVal = val;
    // val = val - delta;
    const maxPercent = 100 - (props.textWithPercent ?? 20);
    let percent1 = (dest / maxVal) * maxPercent;
    percent1 = Math.round(percent1);
    let percent2 = (delta / maxVal) * maxPercent;
    percent2 = Math.round(percent2);
    if (percent2 <= 8) {
      if (percent2 !== 0) {
        percent2 = 20;
        percent1 = percent1 - 20;
      }
    } else if (percent1 === 0 && dest > 0) {
      percent1 = 10;
      percent2 = percent2 - 10;
    }
    if (val === dest) {
      blocks.value = [
        {
          width: percent1 + "%",
          text: "" + roundNumber(val, 2),
          color: props.underColor ?? "grey",
          textColor: props.textColor ?? "white",
          unit: props.unit,
          tooltip: textIst,
        },
        {
          width: (props.textWithPercent ?? 20) + "%",
          text: "" + roundNumber(dest, 2),
          color: props.maxTextBackground ?? "transparent",
          textColor: props.maxTextColor ?? "black",
          unit: props.unit ?? "h",
          tooltip: textSoll,
        },
      ];
    } else {
      blocks.value = [
        {
          width: percent1 + "%",
          text: "" + roundNumber(val, 2),
          color: props.underColor ?? "grey",
          textColor: props.textColor ?? "white",
          unit: props.unit ?? "h",
          tooltip: textIst,
        },
        {
          width: percent2 + "%",
          text: "" + roundNumber(delta, 2),
          color: props.overColor ?? "red",
          textColor: props.textColor ?? "white",
          unit: props.unit ?? "h",
          tooltip: textMehr,
        },
        {
          width: (props.textWithPercent ?? 20) + "%",
          text: "" + roundNumber(dest, 2),
          color: props.maxTextBackground ?? "transparent",
          textColor: props.maxTextColor ?? "black",
          unit: props.unit ?? "h",
          tooltip: textSoll,
        },
      ];
    }
  } else {
    const delta = dest - val;
    const maxVal = dest;
    const maxPercent = 100 - (props.textWithPercent ?? 20);

    let percent1 = (val / maxVal) * maxPercent;
    percent1 = Math.round(percent1);
    let percent2 = (delta / maxVal) * maxPercent;
    percent2 = Math.round(percent2);
    if (percent2 < 8) {
      if (percent2 !== 0) {
        percent2 = 8;
        percent1 = percent1 - 8;
      }
    }
    if (percent1 < 8) {
      if (percent1 !== 0) {
        percent1 = 8;
        percent2 = percent2 - 8;
      }
    }
    if (!percent1) {
      // ist der Wert leer oder undefined - dann auch den Block nicht setzen
      // da sonst die Höhe falsch ist!
      blocks.value = [
        {
          width: percent2 + "%",
          text: "" + roundNumber(delta, 2),
          color: props.remainingColor ?? "lightgrey",
          textColor: props.textColor ?? "white",
          unit: props.unit ?? "h",
          tooltip: textoffen,
        },
        {
          width: (props.textWithPercent ?? 20) + "%",
          text: "" + roundNumber(dest, 2),
          color: props.maxTextBackground ?? "transparent",
          textColor: props.maxTextColor ?? "black",
          unit: props.unit ?? "h",
          tooltip: textSoll,
        },
      ];
    } else {
      blocks.value = [
        {
          width: percent1 + "%",
          text: "" + roundNumber(val, 2),
          color: props.underColor ?? "grey",
          textColor: props.textColor ?? "white",
          unit: props.unit ?? "h",
          tooltip: textIst,
        },
        {
          width: percent2 + "%",
          text: "" + roundNumber(delta, 2),
          color: props.remainingColor ?? "lightgrey",
          textColor: props.textColor ?? "white",
          unit: props.unit ?? "h",
          tooltip: textoffen,
        },
        {
          width: (props.textWithPercent ?? 20) + "%",
          text: "" + roundNumber(dest, 2),
          color: props.maxTextBackground ?? "transparent",
          textColor: props.maxTextColor ?? "black",
          unit: props.unit ?? "h",
          tooltip: textSoll,
        },
      ];
    }
  }
}
</script>
<template>
  <v-card class="d-flex align-content-start flex-wrap pa-0 w-100" width="100%">
    <div
      v-for="(block, ix) in blocks"
      :key="'b' + ix"
      class="block"
      :style="getStyle(block)"
    >
      <v-tooltip location="bottom" :text="block.tooltip">
        <template v-slot:activator="{ props }">
          <span v-bind="props"> {{ block.text }} {{ block.unit }} </span>
        </template>
      </v-tooltip>
    </div>
  </v-card>
</template>

<style scoped>
.container {
  border-radius: 1em;
}
.block {
  border-radius: 0 !important;
  display: inline-block;
  text-align: center;
  vertical-align: middle;
}
.block > span {
  display: inline-block;
  vertical-align: middle;
  line-height: normal;
}
</style>
